// The dashboard's Pulsars section (GET /api/standing-agents/report). Two rows:
//   1. Status strip  — Active / Needs you / Halted / Total, right now
//   2. Activity card — 7 days of check-ins (daily bars), proposals by who decided,
//                      and actions carried out
// Home renders it only while the region has Pulsars on (`features.pulsars`); a null
// report (loading, or a failed load) keeps the em-dash tiles so the row never jumps.

import { Link } from 'react-router-dom';
import type { StandingReport } from '@shared/api-types';
import { parseApiDate } from '@shared/datetime';
import { statusColor } from '../../lib/runStatus';
import { Card, CardHeader, Kpi, SectionHeading } from './tiles';
import { FAILED_HATCH, KPI_GRID, WEEKDAY } from './tileStyles';

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// ── status strip ─────────────────────────────────────────────────────────────

function StatusStrip({ report }: { report: StandingReport | null }) {
  const val = (n: number | undefined) => (report ? (n ?? 0).toLocaleString() : '—');
  const needsYouSub = report
    ? report.needsYou === 0
      ? 'nothing pending'
      : [
          report.pendingProposals > 0 ? plural(report.pendingProposals, 'proposal') : null,
          report.openQuestions > 0 ? plural(report.openQuestions, 'question') : null,
        ].filter(Boolean).join(' · ')
    : undefined;
  const totalSub = report
    ? [plural(report.drafts, 'draft'), report.paused > 0 ? `${report.paused.toLocaleString()} paused` : null]
        .filter(Boolean).join(' · ')
    : undefined;

  return (
    <div style={KPI_GRID}>
      <Kpi label="Active" value={val(report?.active)} sub={report ? 'on their schedule' : undefined} to="/pulsars" />
      <Kpi
        label="Needs you"
        value={val(report?.needsYou)}
        sub={needsYouSub}
        subColor={report && report.needsYou > 0 ? 'var(--warn)' : 'var(--fg-muted)'}
        to="/pulsars?attention=1"
      />
      <Kpi
        label="Halted"
        value={val(report?.halted)}
        sub={report ? (report.halted > 0 ? 'stopped, waiting for a resume' : 'none') : undefined}
        subColor={report && report.halted > 0 ? 'var(--danger)' : 'var(--fg-muted)'}
        to="/pulsars?status=halted"
      />
      <Kpi label="Total Pulsars" value={val(report?.total)} sub={totalSub} to="/pulsars" />
    </div>
  );
}

// ── activity card ────────────────────────────────────────────────────────────

function ColumnHead({ label, value, sub, subColor }: { label: string; value: string; sub: string; subColor?: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 600, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 12, marginTop: 2, color: subColor ?? 'var(--fg-muted)' }}>{sub}</div>
    </div>
  );
}

function CheckInBars({ report }: { report: StandingReport }) {
  const days = report.dailyCheckIns.map((d) => {
    const parsed = parseApiDate(d.date);
    return { ...d, label: WEEKDAY[Number.isNaN(parsed.getTime()) ? 0 : parsed.getUTCDay()] };
  });
  const max = Math.max(1, ...days.map((d) => d.succeeded + d.failed));
  const ok = statusColor('succeeded');
  const bad = statusColor('failed');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 56 }}>
        {days.map((d) => {
          const total = d.succeeded + d.failed;
          const text = `${d.label}: ${d.succeeded} finished, ${d.failed} failed`;
          return (
            <div
              key={d.date}
              role="img"
              aria-label={text}
              title={text}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 2 }}
            >
              {d.failed > 0 && (
                <div style={{ height: `${(d.failed / max) * 100}%`, background: bad, backgroundImage: FAILED_HATCH, borderRadius: '2px 2px 0 0' }} />
              )}
              {d.succeeded > 0 && (
                <div style={{ height: `${(d.succeeded / max) * 100}%`, background: ok, borderRadius: total === d.succeeded ? '2px 2px 0 0' : 0 }} />
              )}
              {total === 0 && <div style={{ height: 2, background: 'var(--border)' }} />}
            </div>
          );
        })}
      </div>
      <div aria-hidden style={{ display: 'flex', marginTop: 4 }}>
        {days.map((d) => (
          <span key={d.date} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'var(--fg-muted)' }}>{d.label}</span>
        ))}
      </div>
    </>
  );
}

// Proposal outcomes in the order a reviewer thinks about them. Each segment carries
// a text count in the legend beside the bar, so colour is never the only cue.
const DECISIONS: { key: keyof StandingReport; label: string; color: string }[] = [
  { key: 'approved7d', label: 'Approved', color: 'var(--success)' },
  { key: 'autoApproved7d', label: 'Auto-approved', color: 'var(--accent)' },
  { key: 'denied7d', label: 'Denied', color: 'var(--danger)' },
  { key: 'expired7d', label: 'Expired', color: 'var(--fg-dim)' },
  { key: 'pending7d', label: 'Waiting for review', color: 'var(--warn)' },
];

function ProposalBreakdown({ report }: { report: StandingReport }) {
  const rows = DECISIONS.map((d) => ({ ...d, count: report[d.key] as number }));
  const total = report.proposals7d;
  const summary = rows.map((r) => `${r.count} ${r.label.toLowerCase()}`).join(', ');

  return (
    <>
      <div
        role="img"
        aria-label={total === 0 ? 'No proposals' : summary}
        style={{ display: 'flex', height: 7, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-hover)', marginBottom: 8 }}
      >
        {total > 0 && rows.filter((r) => r.count > 0).map((r) => (
          <div key={r.key} style={{ width: `${(r.count / total) * 100}%`, background: r.color }} />
        ))}
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((r) => (
          <li key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fg-muted)', padding: '1px 0' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flex: '0 0 auto' }} />
            <span style={{ flex: 1 }}>{r.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: r.count > 0 ? 'var(--fg)' : 'var(--fg-muted)' }}>{r.count.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function ActivityCard({ report }: { report: StandingReport | null }) {
  return (
    <Card>
      <CardHeader title="Pulsar activity · 7 days" link={{ to: '/pulsars', label: 'All Pulsars' }} />
      {!report ? (
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>—</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24 }}>
          <div data-testid="pulsar-checkins">
            <ColumnHead
              label="Check-ins"
              value={report.checkIns7d.toLocaleString()}
              sub={`${report.checkInsFailed7d.toLocaleString()} failed`}
              subColor={report.checkInsFailed7d > 0 ? 'var(--danger)' : undefined}
            />
            <CheckInBars report={report} />
          </div>
          <div data-testid="pulsar-proposals">
            <ColumnHead
              label="Proposals"
              value={report.proposals7d.toLocaleString()}
              sub={report.pending7d > 0 ? `${report.pending7d.toLocaleString()} still waiting for review` : 'all decided'}
              subColor={report.pending7d > 0 ? 'var(--warn)' : undefined}
            />
            <ProposalBreakdown report={report} />
          </div>
          <div data-testid="pulsar-actions">
            <ColumnHead
              label="Actions carried out"
              value={report.actionsDone7d.toLocaleString()}
              sub={`${report.actionsFailed7d.toLocaleString()} failed`}
              subColor={report.actionsFailed7d > 0 ? 'var(--danger)' : undefined}
            />
            <p style={{ fontSize: 12, color: 'var(--fg-muted)', margin: 0, lineHeight: 1.5 }}>
              Approved and auto-approved changes Pulsars made this week. Each one is listed on its Pulsar&rsquo;s
              Activity tab.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── section ──────────────────────────────────────────────────────────────────

export function PulsarsReport({ report }: { report: StandingReport | null }) {
  return (
    <section aria-label="Pulsars">
      <SectionHeading>Pulsars</SectionHeading>
      {report && report.total === 0 ? (
        <Card style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)' }}>
            No Pulsars yet. A Pulsar keeps an agent working toward one goal: it checks in on a schedule and proposes
            changes for you to approve.{' '}
            <Link to="/pulsars/new" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              New Pulsar →
            </Link>
          </p>
        </Card>
      ) : (
        <>
          <StatusStrip report={report} />
          <div style={{ marginBottom: 12 }}>
            <ActivityCard report={report} />
          </div>
        </>
      )}
    </section>
  );
}
